/**
 * LocationCleanupModal (Jul-2026, User-Wunsch) — Standort-Dubletten bereinigen
 * auf Händler-Ebene (Hauptsitz): offizielle Standorte (Registrierung) sind die
 * Referenz; ähnlich lautende Fahrzeug-Standorte (Import/Sync) werden manuell
 * zugeordnet. Fremde Händler bleiben unberührt.
 */
import { useEffect, useState } from "react";
import { MapPin, Loader2, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

export default function LocationCleanupModal({ open, onOpenChange, onDone }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [targets, setTargets] = useState({});

    const load = async () => {
        setData(null);
        try {
            const { data: d } = await api.get("/dealer/locations/cleanup");
            setData(d);
            const init = {};
            (d.variants || []).forEach((v) => {
                if (!v.matches_official && v.suggested_target) init[v.location] = v.suggested_target;
            });
            setTargets(init);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Standorte konnten nicht geladen werden");
        }
    };
    useEffect(() => { if (open) load(); }, [open]);

    const merge = async (variants, target) => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/dealer/locations/merge", { target, variants });
            toast.success(`${r.updated} Fahrzeug(e) auf „${target}" vereinheitlicht`);
            await load();
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const applyAll = async () => {
        const jobs = {};
        Object.entries(targets).forEach(([loc, tgt]) => {
            if (tgt) (jobs[tgt] = jobs[tgt] || []).push(loc);
        });
        const total = Object.values(jobs).reduce((a, b) => a + b.length, 0);
        if (!total) { toast.info("Keine offenen Zuordnungen"); return; }
        if (!window.confirm(`${total} Standort-Schreibweise(n) den gewählten Zielen zuordnen?`)) return;
        setBusy(true);
        try {
            for (const [tgt, vars] of Object.entries(jobs)) {
                await api.post("/dealer/locations/merge", { target: tgt, variants: vars });
            }
            toast.success("Alle Zuordnungen übernommen");
            await load();
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const openVariants = (data?.variants || []).filter((v) => !v.matches_official);
    const okVariants = (data?.variants || []).filter((v) => v.matches_official);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="location-cleanup-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> Standorte bereinigen</DialogTitle>
                    <DialogDescription>
                        Referenz sind die offiziellen Standorte deiner Organisation (Hauptsitz + Filialen).
                        Ähnlich lautende Fahrzeug-Standorte aus Import/Synchronisation kannst du hier zuordnen —
                        Standorte fremder Händler bleiben unberührt.
                    </DialogDescription>
                </DialogHeader>
                {!data ? (
                    <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" /></div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Offizielle Standorte ({(data.official || []).length})</div>
                            <div className="flex flex-wrap gap-1.5" data-testid="official-sites">
                                {(data.official || []).map((o) => (
                                    <span key={o.label} className="inline-flex items-center gap-1 text-[11px] font-bold bg-gray-100 text-gray-700 rounded-sm px-2 py-1">
                                        {o.site_code && <span className="text-[9px] bg-[#111827] text-white rounded-sm px-1 py-0.5 font-mono-num">{o.site_code}</span>}
                                        {o.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Abweichende Fahrzeug-Standorte ({openVariants.length})</div>
                                {openVariants.length > 0 && (
                                    <Button size="sm" onClick={applyAll} disabled={busy} className="h-7 rounded-sm bg-[#111827] text-white text-xs font-bold" data-testid="location-apply-all-btn">
                                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />} Alle Vorschläge übernehmen
                                    </Button>
                                )}
                            </div>
                            {openVariants.length === 0 ? (
                                <div className="text-sm text-gray-500 py-3" data-testid="location-cleanup-clean">Alles sauber — keine abweichenden Schreibweisen gefunden. ✅</div>
                            ) : (
                                <ul className="space-y-1.5" data-testid="location-variants">
                                    {openVariants.map((v) => (
                                        <li key={v.location} className="border border-gray-200 rounded-sm px-2.5 py-2 flex items-center gap-2 flex-wrap" data-testid={`location-variant-${v.location}`}>
                                            <span className="font-bold text-sm truncate">{v.location}</span>
                                            <span className="text-[10px] font-mono-num text-gray-400">{v.count}× Fahrzeug(e)</span>
                                            {v.similarity >= 0.55 && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5">{Math.round(v.similarity * 100)} % ähnlich</span>}
                                            <div className="ml-auto flex items-center gap-1.5">
                                                <select
                                                    value={targets[v.location] || ""}
                                                    onChange={(e) => setTargets((s) => ({ ...s, [v.location]: e.target.value }))}
                                                    className="h-8 text-xs font-bold rounded-sm border border-gray-300 bg-white px-1.5 max-w-[260px]"
                                                    data-testid={`location-target-${v.location}`}
                                                >
                                                    <option value="">— Ziel wählen —</option>
                                                    {(data.official || []).map((o) => (
                                                        <option key={o.label} value={o.label}>{o.site_code ? `${o.site_code} · ` : ""}{o.label}</option>
                                                    ))}
                                                </select>
                                                <Button size="sm" disabled={busy || !targets[v.location]}
                                                    onClick={() => merge([v.location], targets[v.location])}
                                                    className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                                                    data-testid={`location-merge-${v.location}`}>
                                                    <Check className="w-3.5 h-3.5 mr-1" /> Zuordnen
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {okVariants.length > 0 && (
                            <div className="text-[11px] text-gray-400">{okVariants.length} Standort-Schreibweise(n) entsprechen bereits einem offiziellen Standort.</div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
